/**
 * Global ARIA-live announcer for screen readers.
 *
 * Renders a single visually-hidden polite region (mounted once in Layout)
 * that any code path can update via `announce()`. Use for things screen-
 * reader users would otherwise miss — cart updates, language switches,
 * async results — that aren't already covered by an explicit aria-live
 * element (e.g. sonner toasts).
 */
import { useEffect, useState } from 'react';

type Listener = (message: string) => void;
const listeners = new Set<Listener>();

/** Announce a message to assistive tech. Safe to call from anywhere. */
export function announce(message: string) {
  if (!message) return;
  listeners.forEach((l) => l(message));
}

/** Mount once at the top of the app tree. */
export function LiveAnnouncer() {
  const [message, setMessage] = useState('');

  useEffect(() => {
    const listener: Listener = (msg) => {
      // Reset to '' first so identical consecutive messages are re-announced.
      setMessage('');
      // Next tick so the DOM mutation is detected as a change.
      requestAnimationFrame(() => setMessage(msg));
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {message}
    </div>
  );
}
